import { useQuery, useMutation } from '@apollo/client';
import React, { useEffect, useState } from 'react';
import { Helmet } from 'react-helmet';
import HeroSection from 'components/HeroSection';
import { PageWrapper } from 'components/styled';
import ApplicationContainer from 'components/ApplicationContainer';
import { down } from 'styled-breakpoints';
import {
  MultiSelectInput,
  TextInput,
  CheckboxInput,
  SelectInput,
  InputWrapper,
  FieldLabel,
  StyledInput,
  ErrorMessage,
} from 'components/Form';
import { Col, Container, Row } from 'reactstrap';
import { H2, P } from 'components/Typography';
import { Field, Form } from 'react-final-form';
import { Link } from 'react-router-dom';
import styled from 'styled-components/macro';
import Button from 'components/Button';
import { theme } from 'styled-tools';
import Alert from 'components/Alerts';
import { WARNING_TYPE } from 'components/Alerts';
import SmallTooltip from 'components/Tooltips/SmallTooltip';
import MobileNumberVerificationWrapper from 'components/MobileNumber/MobileNumberVerificationWrapper';
import useRenewJWT from 'services/Auth/useRenewJWT';
import { isValidEmail } from 'utils/validators';
import { isValidMobileNumber, removeCountryCode } from 'utils/mobileNumberValidation';
import { getLocalStorageData, setLocalStorageData } from 'utils/localStorage';
import { userIsContracted, NO_CONTRACT_MESSAGE } from 'utils/roles';
import { useMobileNumberVerification } from 'hooks/useMobileNumberVerification/useMobileNumberVerification';
import { GET_AGENCIES, GET_PROFILE_INFO, UPDATE_USER } from './graphql';
import { mobileOptinText } from 'components/MobileNumber/const';
import MobileNumberVerificationStatus from 'components/MobileNumber/MobileNumberVerificationStatus';
import ConfirmedUnconfirmed from 'components/MobileNumber/confirmedUnconfirmed';

const AppContainer = styled(ApplicationContainer)`
  display: flex;
  justify-content: center;
`;

const requiredFields = [
  { name: 'firstName', label: 'First Name' },
  { name: 'lastName', label: 'Last Name' },
  { name: 'email', label: 'Email' },
  { name: 'occupations', label: 'Occupations' },
  { name: 'memberAgencyState', label: 'Employer State' },
  { name: 'memberAgency', label: 'Employer' },
];

function validate(values) {
  let errors = {};

  errors = requiredFields.reduce((errs, field) => {
    if (!values[field.name]) {
      errs[field.name] = `${field.label} is required.`;
    }
    return errs;
  }, errors);

  if (values.email && !isValidEmail(values.email)) {
    errors.email = 'Please enter a valid email.';
  }

  if (values.recoveryEmail && !isValidEmail(values.recoveryEmail)) {
    errors.recoveryEmail = 'Please enter a valid email.';
  }

  if (values.recoveryEmail) {
    if (!values.recoveryEmailConfirm) {
      errors.recoveryEmailConfirm = 'Confirm your recovery email.';
    }

    if (values.recoveryEmail && values.recoveryEmailConfirm && values.recoveryEmail !== values.recoveryEmailConfirm) {
      errors.recoveryEmailConfirm = 'Entry must match Recovery Email.';
    }

    if (values.recoveryEmailConfirm && !isValidEmail(values.recoveryEmailConfirm)) {
      errors.recoveryEmailConfirm = 'Please enter a valid email.';
    }
  }

  const hasMobileNumber = values.mobileNumber?.trim();

  if (hasMobileNumber) {
    // Validate mobile number format
    if (hasMobileNumber && !isValidMobileNumber(values.mobileNumber)) {
      errors.mobileNumber = ' ';
    }
  }

  return errors;
}

function prepInitialValues(user, occupations) {
  return {
    ...user,
    memberAgencyState: user.state.id,
    occupations: user.occupations.map(occupation => {
      return {
        id: occupation.id,
        value: occupation.id,
        label: occupations.find(occ => occ.id === occupation.id).name,
      };
    }),
    memberAgency: user.memberAgency && user.memberAgency.id,
    recoveryEmail: user.recoveryEmail,
    recoveryEmailConfirm: user.recoveryEmail,
    masaMember: user.isMasaMember,
    showGettingStarted: !getLocalStorageData('dismissGettingStarted', true),
    mobileNumber: removeCountryCode(user.mobileNumber),
    smsOptIn: user.smsOptIn,
    isMobileNumberVerified: user.isMobileNumberVerified,
  };
}

export const FormRow = styled(Row)`
  margin-bottom: 1rem;

  ${down('lgMobile')} {
    margin-bottom: 1rem;

    [class^='col-'] {
      margin-bottom: 1rem;
    }
  }
`;

export const FormHeader = styled(H2)`
  margin-bottom: 1rem;
`;

const StyledRow = styled(Row)`
  margin-top: 1rem;
  padding: 0;
`;

const ProfileFormContainer = styled.div`
  max-width: 30rem;
  font-size: 0.875rem;
  background: ${theme('colors.white')};
  border-radius: ${theme('borderRadius')};
  padding-top: 0.75rem;
  box-shadow: ${theme('boxShadow')};
  padding-left: 1rem;
  padding-right: 1rem;
`;

export const ProfileForm = styled.form`
  display: flex;
  flex-direction: column;
  justify-content: space-between;
`;

const ProfileButton = styled(Button)`
  width: 100%;
  background-color: ${theme('colors.primary')};
  margin-bottom: 3rem;
`;

const ProfileLabel = styled.label`
  font-weight: ${theme('fontWeightBold')};
`;

const StyledLink = styled(Link)`
  text-decoration: underline;
`;

const EmailOptInMessage = styled.span`
  color: ${theme('colors.darkRed')};
`;

function Profile() {
  const [user, setUser] = useState(null);
  const [states, setStates] = useState(null);
  const [agencies, setAgencies] = useState(null); // eslint-disable-line no-unused-vars
  const [occupations, setOccupations] = useState(null);
  const [isSuccess, setIsSuccess] = useState(false);
  const [error, setError] = useState('');
  const [updateUser] = useMutation(UPDATE_USER);
  const [currentState, setCurrentState] = useState(''); // eslint-disable-line no-unused-vars
  const [userReceivesEmails, setUserReceivesEmails] = useState(true);
  const [form, setForm] = useState(null);
  const renewJWT = useRenewJWT();
  const {
    showVerificationModal,
    setShowVerificationModal,
    mobileNumberToVerify,
    isMobileNumberVerified,
    setIsMobileNumberVerified,
    mobileNumbersMatch,
    handleSendVerificationCode,
    errorVerification,
    currentValues,
    setErrorVerification,
    isMobileNumberValid,
    sendMobileVerificationCodeLoading,
  } = useMobileNumberVerification(form, user?.isMobileNumberVerified || false);

  useQuery(GET_PROFILE_INFO, {
    fetchPolicy: 'no-cache',
    onCompleted: data => {
      setUser(data.myUser);
      setUserReceivesEmails(data.myUser.emailOpIn ? data.myUser.emailOpIn.receiveEmails : true);
      setStates(data.states);
      setOccupations(data.occupations);
      setCurrentState(data.myUser.state.name);
    },
    onError: err => console.error(err),
  });

  useQuery(GET_AGENCIES, {
    skip: !user,
    variables: { stateId: user && user.state.id },
    onCompleted: data => {
      setAgencies(data.agencies);
    },
    onError: err => console.error(err),
  });

  const onSubmit = async values => {
    setError('');
    setIsSuccess(false);

    // Only require verification if a mobile number is actually provided (not empty)
    const hasMobileNumber = values.mobileNumber?.trim();
    if (hasMobileNumber && !isMobileNumberVerified) {
      setError('Mobile Number must be verified');
      return;
    }

    setLocalStorageData('dismissGettingStarted', !values.showGettingStarted, true);

    const variables = {
      id: user.id,
      firstName: values.firstName,
      lastName: values.lastName,
      email: values.email,
      occupations: values.occupations.map(occ => occ.id),
      state: values.memberAgencyState,
      memberAgency: values.memberAgency,
      isMasaMember: values.masaMember,
      recoveryEmail: values.recoveryEmail,
      mobileNumber: values.mobileNumber?.trim() || '',
      smsOptIn: values.smsOptIn,
      isMobileNumberVerified: isMobileNumberVerified,
    };

    try {
      const res = await updateUser({ variables: variables });
      const { user } = res.data.updateUser;

      await renewJWT();
      setIsSuccess(true);
      setUser(user);
      setUserReceivesEmails(user.emailOpIn ? user.emailOpIn.receiveEmails : true);
      setLocalStorageData('userReceivesEmails', user.emailOpIn ? user.emailOpIn.receiveEmails : true);

      if (form) {
        form.change('smsOptIn', user.smsOptIn);
        form.change('mobileNumber', removeCountryCode(user.mobileNumber) || '');
      }

      window.scrollTo({ top: 0, behavior: 'smooth' });
    } catch (err) {
      const message = err.message.replace('GraphQL error: ', '');
      setError(message);
      setIsSuccess(false);
    }
  };

  useEffect(() => {
    if (errorVerification) {
      setError(errorVerification);
    } else {
      setError('');
    }
  }, [errorVerification, isMobileNumberVerified]);

  const savedMobileNumber = removeCountryCode(user?.mobileNumber);

  // Simple: scroll to error whenever error state changes
  useEffect(() => {
    if (error) {
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  }, [error]);

  return (
    <PageWrapper>
      <Helmet>
        <title>Profile</title>
      </Helmet>
      <HeroSection>
        <h1>Welcome to Your Profile.</h1>
      </HeroSection>
      {!userIsContracted() && (
        <Container>
          <StyledRow>
            <Col sm={12}>
              <Alert type={WARNING_TYPE} alertText={NO_CONTRACT_MESSAGE} />
            </Col>
          </StyledRow>
        </Container>
      )}
      <AppContainer>
        {user && occupations && states && (
          <Form
            onSubmit={onSubmit}
            validate={validate}
            initialValues={prepInitialValues(user, occupations)}
            initialValuesEqual={() => true}
            render={({ form: formApi, handleSubmit, submitting, touched, errors, values }) => {
              if (!form) {
                setForm(formApi);
              }

              return (
                <ProfileFormContainer>
                  <FormHeader>Profile</FormHeader>
                  {error && <Alert type='danger' alertText={error} />}
                  {isSuccess && <Alert type='success' alertText={'Update successful.'} />}
                  <ProfileForm onSubmit={handleSubmit}>
                    <FormRow>
                      <Col md={6}>
                        <TextInput
                          label='First Name'
                          name='firstName'
                          placeholder='First Name'
                          value={user.firstName}
                        />
                      </Col>
                      <Col md={6}>
                        <TextInput label='Last Name' name='lastName' placeholder='Last Name' value={user.lastName} />
                      </Col>
                    </FormRow>
                    <FormRow>
                      <Col md={12}>
                        <TextInput
                          fieldClasses={!userReceivesEmails && 'email-input'}
                          label='Email'
                          name='email'
                          placeholder='Email'
                          value={user.email}
                        />
                      </Col>
                    </FormRow>
                    {!userReceivesEmails && (
                      <FormRow>
                        <Col md={12}>
                          <EmailOptInMessage>
                            Emails are disabled for this account. Please update your email address to begin receiving
                            notifications.
                          </EmailOptInMessage>
                        </Col>
                      </FormRow>
                    )}
                    {user?.aliases?.length > 0 && (
                      <FormRow>
                        <Col md={12}>
                          <ProfileLabel>Email Aliases</ProfileLabel>{' '}
                          <SmallTooltip
                            placement='right'
                            id={`email_alias_tooltip`}
                            text='If you do not recognize these emails, please contact Infinitec Support.'
                          />
                          :
                        </Col>
                        <Col md={12}>
                          {user.aliases.map(({ email }, i) => (
                            <P key={email}>{email}</P>
                          ))}
                        </Col>
                      </FormRow>
                    )}
                    <FormRow>
                      <Col md={12}>
                        {occupations && (
                          <MultiSelectInput
                            label='Occupations'
                            name='occupations'
                            options={occupations.map(occupation => {
                              return { value: occupation.id, label: occupation.name };
                            })}
                          />
                        )}
                      </Col>
                    </FormRow>
                    <FormRow>
                      <Col md={12}>
                        <ProfileLabel>State: </ProfileLabel> {user.state.name}
                      </Col>
                    </FormRow>
                    <FormRow>
                      <Col md={12}>
                        {agencies && (
                          <SelectInput
                            label='Employer'
                            name='memberAgency'
                            onChange={e => {
                              setUser({ ...user, memberAgency: { ...user.memberAgency, id: e.currentTarget.value } });
                            }}
                            options={agencies.map(agency => {
                              return { value: agency.id, label: agency.name };
                            })}
                          />
                        )}
                      </Col>
                    </FormRow>
                    <FormRow>
                      <Col md={12} role='group' aria-label='Show Getting Started Video'>
                        <CheckboxInput name={'showGettingStarted'}>Show Getting Started video </CheckboxInput>
                      </Col>
                    </FormRow>
                    {user.isMasaMember && (
                      <FormRow>
                        <Col md={12}>
                          <ProfileLabel>&#x2713; MASA member </ProfileLabel>
                        </Col>
                      </FormRow>
                    )}
                    <div>
                      <H2>Security Info</H2>
                      <P>
                        To change your password, <StyledLink to='/forgot-password'>click here</StyledLink>
                      </P>
                    </div>
                    <FormRow>
                      <Col xs='12' md={isMobileNumberVerified ? 12 : 6}>
                        <Field name='mobileNumber' type='text'>
                          {({ input, meta }) => {
                            const handleChange = e => {
                              const newValue = e.target.value;
                              input.onChange(e);
                              setIsMobileNumberVerified(false);

                              // Auto opt-out when mobile number is removed
                              const hasMobileNumber = newValue?.trim();
                              if (!hasMobileNumber && values.smsOptIn) {
                                formApi.change('smsOptIn', false);
                              }
                            };

                            return (
                              <InputWrapper>
                                <FieldLabel htmlFor='mobileNumber' $flexSpace>
                                  Mobile Number
                                  {input.value.length > 0 && (
                                    <ConfirmedUnconfirmed
                                      mobileNumber={input.value}
                                      isMobileNumberVerified={isMobileNumberVerified}
                                    />
                                  )}
                                  <MobileNumberVerificationWrapper
                                    form={form}
                                    isMobileNumberValid={isMobileNumberValid}
                                    isMobileNumberVerified={isMobileNumberVerified}
                                    setIsMobileNumberVerified={setIsMobileNumberVerified}
                                    showVerificationModal={showVerificationModal}
                                    setShowVerificationModal={setShowVerificationModal}
                                    mobileNumberToVerify={mobileNumberToVerify}
                                    handleSendVerificationCode={handleSendVerificationCode}
                                    sendMobileVerificationCodeLoading={sendMobileVerificationCodeLoading}
                                    currentValues={currentValues}
                                    setErrorVerification={setErrorVerification}
                                  />
                                </FieldLabel>

                                <StyledInput
                                  {...input}
                                  onChange={handleChange}
                                  id='mobileNumber'
                                  invalid={meta.error && meta.touched}
                                  placeholder='Mobile Phone'
                                  className={meta.error && meta.touched && 'input-invalid'}
                                  autoComplete='new-no'
                                />

                                {meta.error && meta.touched && <ErrorMessage>{meta.error}</ErrorMessage>}
                              </InputWrapper>
                            );
                          }}
                        </Field>
                      </Col>
                      {isMobileNumberVerified ? (
                        <></>
                      ) : (
                        <Col xs='12' md='6'>
                          <FieldLabel style={{ marginTop: '1.4rem', width: '100%' }}>
                            <MobileNumberVerificationStatus
                              isMobileNumberVerified={isMobileNumberVerified}
                              isMobileNumberValid={isMobileNumberValid}
                              onSendVerificationCode={handleSendVerificationCode}
                              isLoading={sendMobileVerificationCodeLoading}
                              currentValues={currentValues}
                              setErrorVerification={setErrorVerification}
                            />
                          </FieldLabel>
                        </Col>
                      )}
                    </FormRow>
                    <FormRow style={{ marginTop: '0.5rem' }}>
                      <Col>
                        <CheckboxInput name='smsOptIn'>{mobileOptinText}</CheckboxInput>
                      </Col>
                    </FormRow>
                    <div>
                      <ProfileButton kind={'primary'} disabled={submitting} type='submit'>
                        Submit
                      </ProfileButton>
                    </div>
                  </ProfileForm>
                </ProfileFormContainer>
              );
            }}
          />
        )}
      </AppContainer>
    </PageWrapper>
  );
}

export default React.memo(Profile);
